import { useMemo, useState, useCallback } from "react";
import { Card, CardContent } from "../../../components/ui-custom/Card";
import { Badge } from "../../../components/ui-custom/Badge";
import { Progress } from "../../../components/ui-custom/Progress";
import {
  Activity,
  TrendingDown,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Users,
  Package,
  Wrench,
  FileText,
  Loader2,
  IndianRupee,
} from "lucide-react";
import { formatCurrency } from "./boqData";
import { BoQApi } from "../../../api/boq/service";
import { padLeadingZeroes } from "utils/utilityFunctions";

// ─── Status helpers ───────────────────────────────────────────────────────────

const getExecutionStatus = (item) => {
  if (item.actualCost > 0 && item.taskId) return { label: "Live", value: "in-progress" };
  if (item.actualCost > 0) return { label: "Spent", value: "in-progress" };
  if (item.taskId) return { label: "Linked", value: "completed" };
  return { label: "Planned", value: "planned" };
};

const statusClassNames = {
  planned: "bg-muted text-muted-foreground text-[10px]",
  "in-progress": "bg-warning/10 text-warning text-[10px]",
  completed: "bg-success/10 text-success text-[10px]",
};

// ─── History entry helpers ─────────────────────────────────────────────────────

const historyTypeConfig = {
  worker: {
    icon: <Users className="h-3.5 w-3.5" />,
    label: "Worker",
    color: "text-blue-500",
    bg: "bg-blue-500/10",
  },
  material: {
    icon: <Package className="h-3.5 w-3.5" />,
    label: "Material",
    color: "text-amber-500",
    bg: "bg-amber-500/10",
  },
  general: {
    icon: <Wrench className="h-3.5 w-3.5" />,
    label: "General",
    color: "text-purple-500",
    bg: "bg-purple-500/10",
  },
  workorder: {
    icon: <FileText className="h-3.5 w-3.5" />,
    label: "Work Order",
    color: "text-green-500",
    bg: "bg-green-500/10",
  },
};

const getHistorySubtitle = (entry) => {
  switch (entry.type) {
    case "worker":
      return `${entry.worker_name} · ${entry.worker_workname}`;
    case "material":
      return `${entry.material_name}${entry.material_subcategory ? ` (${entry.material_subcategory})` : ""} · ${Math.abs(entry.quantity)} ${entry.uom}`;
    case "general":
      return entry.transaction_type || "General Expense";
    case "workorder":
      return `${entry.contractor_name} · WO#${padLeadingZeroes(entry.workorder_id, 3)}`;
    default:
      return "—";
  }
};

const formatPaymentDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatTime = (datetimeStr) => {
  if (!datetimeStr) return null;
  try {
    return new Date(datetimeStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
};

const getEntryGroupDate = (entry) =>
  entry.payment_date || entry.date || entry.transaction_date || entry.created_on || null;

const getDateGroupKey = (entry) => {
  const rawDate = getEntryGroupDate(entry);
  if (!rawDate) return "unknown";

  try {
    if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      return rawDate;
    }
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return String(rawDate);
    return parsed.toISOString().slice(0, 10);
  } catch {
    return String(rawDate);
  }
};

const getEntrySortTime = (entry) => {
  const rawDate = getEntryGroupDate(entry);
  if (!rawDate) return 0;
  const parsed = new Date(rawDate).getTime();
  if (!Number.isNaN(parsed)) return parsed;
  return 0;
};

const getEntryTimeLabel = (entry) =>
  formatTime(entry.created_on || entry.payment_date || entry.transaction_date || entry.date);

const getPaidByLabel = (entry) =>
  entry.paid_by || entry.paid_by_name || entry.created_by || "—";

const getRemarksLabel = (entry) => entry.remarks || entry.comments || "—";

// ─── HistoryEntry component ───────────────────────────────────────────────────

const HistoryEntry = ({ entry }) => {
  const config = historyTypeConfig[entry.type] || historyTypeConfig.general;
  const remarks = getRemarksLabel(entry);
  const paidBy = getPaidByLabel(entry);
  const time = getEntryTimeLabel(entry);

  return (
    <tr className="border-b border-border/10 last:border-b-0 hover:bg-muted/20">
      <td className="px-3 py-3 align-top">
        <div className="flex items-center gap-2">
          <div className={`flex-shrink-0 rounded-full p-1.5 ${config.bg}`}>
            <span className={config.color}>{config.icon}</span>
          </div>
          <div className="min-w-0">
            <div className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>
              {config.label}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">
              {entry.transaction_method || ""}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <p className="text-xs font-medium text-foreground break-words">
          {getHistorySubtitle(entry)}
        </p>
      </td>
      <td className="px-3 py-3 align-top">
        <p className="text-xs text-muted-foreground break-words">{remarks}</p>
      </td>
      <td className="px-3 py-3 align-top">
        <p className="text-xs text-foreground">{paidBy.includes("|") ? paidBy.split("|")[1] : paidBy}</p>
      </td>
      <td className="px-3 py-3 align-top">
        <p className="text-xs text-muted-foreground whitespace-nowrap">{time || "—"}</p>
      </td>
      <td className="px-3 py-3 align-top text-right">
        <span className="text-sm font-semibold text-foreground whitespace-nowrap">
          {formatCurrency(entry.amount)}
        </span>
      </td>
    </tr>
  );
};

const HistoryDateGroup = ({ dateLabel, entries, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const totalAmount = entries.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);

  return (
    <div className="rounded-xl border border-border/40 bg-background overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 bg-muted/20 px-4 py-3 text-left transition-colors hover:bg-muted/30"
      >
        <div className="flex min-w-0 items-center gap-3">
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{dateLabel}</p>
            <p className="text-[11px] text-muted-foreground">
              {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</p>
          <p className="text-sm font-semibold text-foreground">{formatCurrency(totalAmount)}</p>
        </div>
      </button>

      {open && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-background">
              <tr className="border-b border-border/10 text-left">
                <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Type
                </th>
                <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Description
                </th>
                <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Remarks
                </th>
                <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Paid by
                </th>
                <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Time
                </th>
                <th className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <HistoryEntry
                  key={`${entry.type || "entry"}-${entry.id || entry.created_on || idx}`}
                  entry={entry}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── CostBreakdown panel ──────────────────────────────────────────────────────

const CostBreakdown = ({ item, history, historyLoading }) => {
  // Derive actual material / labour / other from history
  const actualMaterial = history
    .filter((e) => e.type === "material")
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const actualLabour = history
    .filter((e) => e.type === "worker")
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const estMaterial = item.materialCost || 0;
  const estLabour = item.labourCost || 0;
  const estTotal = item.totalCost || 0;
  const actTotal = item.actualCost || 0;

  const overallPct = estTotal > 0 ? Math.min(100, Math.round((actTotal / estTotal) * 100)) : 0;
  const groupedHistory = useMemo(() => {
    const sortedHistory = [...history].sort(
      (a, b) => getEntrySortTime(b) - getEntrySortTime(a),
    );

    const groups = sortedHistory.reduce((acc, entry) => {
      const key = getDateGroupKey(entry);
      if (!acc[key]) acc[key] = [];
      acc[key].push(entry);
      return acc;
    }, {});

    return Object.entries(groups)
      .sort(([a], [b]) => {
        if (a === "unknown") return 1;
        if (b === "unknown") return -1;
        return new Date(b).getTime() - new Date(a).getTime();
      })
      .map(([key, entries], index) => ({
        key,
        entries,
        defaultOpen: index === 0,
        label:
          key === "unknown"
            ? "Date unavailable"
            : formatPaymentDate(key) || key,
      }));
  }, [history]);

  return (
    <div className="border-t border-border/20 bg-muted/10 px-4 py-4 space-y-4">
      {/* ── Cost Summary Columns ─────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {/* Material */}
        <div className="rounded-lg border border-border/40 bg-sm-card p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500">
            <Package className="h-3 w-3" /> Material Cost
          </div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <div>
              <p className="text-[10px] text-muted-foreground">Estimate</p>
              <p className="text-sm font-bold text-foreground">{formatCurrency(estMaterial)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Actual</p>
              <p className={`text-sm font-bold ${actualMaterial > estMaterial ? "text-destructive" : "text-success"}`}>
                {formatCurrency(actualMaterial)}
              </p>
            </div>
          </div>
        </div>

        {/* Labour */}
        <div className="rounded-lg border border-border/40 bg-sm-card p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-500">
            <Users className="h-3 w-3" /> Labour Cost
          </div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <div>
              <p className="text-[10px] text-muted-foreground">Estimate</p>
              <p className="text-sm font-bold text-foreground">{formatCurrency(estLabour)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Actual</p>
              <p className={`text-sm font-bold ${actualLabour > estLabour ? "text-destructive" : "text-success"}`}>
                {formatCurrency(actualLabour)}
              </p>
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="rounded-lg border border-border/40 bg-sm-card p-3 space-y-1 col-span-2 sm:col-span-1">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-sm-primary">
            <IndianRupee className="h-3 w-3" /> Total Cost
          </div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <div>
              <p className="text-[10px] text-muted-foreground">Estimated</p>
              <p className="text-sm font-bold text-foreground">{formatCurrency(estTotal)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Actual</p>
              <p className={`text-sm font-bold ${actTotal > estTotal ? "text-destructive" : "text-success"}`}>
                {formatCurrency(actTotal)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Progress value={overallPct} className="h-1.5 flex-1" />
            <span className="text-[10px] font-medium text-muted-foreground">{overallPct}%</span>
          </div>
        </div>
      </div>

      {/* ── Execution History ────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Execution History
        </p>
        {historyLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading history…
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center">No transactions recorded yet.</p>
        ) : (
          <div className="space-y-3">
            {groupedHistory.map((group) => (
              <HistoryDateGroup
                key={group.key}
                dateLabel={group.label}
                entries={group.entries}
                defaultOpen={group.defaultOpen}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Single expandable row ────────────────────────────────────────────────────

const ItemRow = ({ item, colsClass }) => {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);

  const budget = item.totalCost;
  const variance = budget - item.actualCost;
  const isOverBudget = variance < 0;

  const handleToggle = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !historyFetched) {
      try {
        setHistoryLoading(true);
        const data = await BoQApi.getItemHistory(item.id);
        setHistory(data);
      } catch (err) {
        console.error("Failed to load item history", err);
      } finally {
        setHistoryLoading(false);
        setHistoryFetched(true);
      }
    }
  }, [expanded, historyFetched, item.id]);

  return (
    <div className="border-t border-border/20">
      {/* Main row */}
      <div
        className={`grid ${colsClass} gap-3 px-4 py-3 hover:bg-muted/20 items-center cursor-pointer transition-colors select-none`}
        onClick={handleToggle}
      >
        {/* Chevron */}
        <div className="flex items-center justify-center">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>

        {/* Code */}
        <span className="text-xs font-mono text-sm-primary font-medium bg-sm-primary/10 px-1.5 py-0.5 rounded text-center truncate">
          {item.code}
        </span>

        {/* Description */}
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{item.description}</p>
          <p className="text-[10px] text-muted-foreground truncate">{item.sectionName}</p>
        </div>

        {/* Planned Qty */}
        <div className="text-right">
          <p className="text-sm font-medium">{item.quantity}</p>
          <p className="text-[10px] text-muted-foreground">{item.unit}</p>
        </div>

        {/* Budget */}
        <span className="text-sm text-right">{formatCurrency(budget)}</span>

        {/* Actual */}
        <span className="text-sm text-right">{formatCurrency(item.actualCost)}</span>

        {/* Variance */}
        <span
          className={`text-sm text-right font-medium flex items-center justify-end gap-1 ${
            isOverBudget ? "text-destructive" : "text-success"
          }`}
        >
          {isOverBudget ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
          {formatCurrency(Math.abs(variance))}
        </span>

        {/* Status */}
        <div className="flex items-center justify-center">
          <Badge className={statusClassNames[item.status.value]}>{item.status.label}</Badge>
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <CostBreakdown item={item} history={history} historyLoading={historyLoading} />
      )}
    </div>
  );
};

// ─── Item table (header + rows) ───────────────────────────────────────────────

const COLS = "grid-cols-[24px_60px_1fr_80px_90px_90px_90px_90px]";

const ItemTable = ({ items, emptyText }) => (
  <CardContent className="p-0 mt-3">
    {/* Header */}
    <div
      className={`grid ${COLS} gap-3 px-4 py-2.5 bg-muted/30 text-[11px] font-medium text-muted-foreground uppercase tracking-wider`}
    >
      <span />
      <span>Code</span>
      <span>Item</span>
      <span className="text-right">Planned</span>
      <span className="text-right">Budget</span>
      <span className="text-right">Actual</span>
      <span className="text-right">Variance</span>
      <span className="text-center">Status</span>
    </div>

    {items.length === 0 ? (
      <div className="py-8 text-center text-sm text-muted-foreground">{emptyText}</div>
    ) : (
      items.map((item) => <ItemRow key={item.id} item={item} colsClass={COLS} />)
    )}
  </CardContent>
);

// ─── Main component ───────────────────────────────────────────────────────────

const BoQExecutionTracking = ({ sections, refreshing }) => {
  const allItems = useMemo(
    () =>
      sections.flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          sectionId: section.id,
          sectionName: section.name,
          sectionCode: section.code,
          status: getExecutionStatus(item),
        }))
      ),
    [sections]
  );

  const liveItems = allItems.filter((item) => item.actualCost > 0 || item.taskId);
  const plannedItems = allItems.filter((item) => item.actualCost <= 0 && !item.taskId);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Execution Tracking</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {refreshing && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          <span>Click any row to expand details</span>
        </div>
      </div>

      {/* Active / Linked */}
      <Card className="border-border/60 shadow-none overflow-hidden">
        <div className="flex items-center gap-2 p-4 pb-0">
          <Activity className="h-4 w-4 text-warning" />
          <h3 className="text-sm font-semibold text-foreground">
            Active / Linked ({liveItems.length})
          </h3>
        </div>
        <ItemTable items={liveItems} emptyText="No active execution data yet" />
      </Card>

      {/* Planned */}
      <Card className="border-border/60 shadow-none overflow-hidden">
        <div className="flex items-center gap-2 p-4 pb-0">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">
            Planned ({plannedItems.length})
          </h3>
        </div>
        <ItemTable items={plannedItems} emptyText="No planned items" />
      </Card>
    </div>
  );
};

export default BoQExecutionTracking;
